/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import React, { useState } from 'react';
import { API_BASE_URL } from '../../utils/apiBaseUrl.ts';
import { auth } from '../../firebase.js';

const CollectionHandlesManager = ({ userIsAdmin }) => {
  const [userEmail, setUserEmail] = useState('');
  const [collectionHandles, setCollectionHandles] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  if (!userIsAdmin) {
    return null; // Don't show to non-admins
  }

  const handleUpdateCollectionHandles = async () => {
    if (!userEmail.trim()) {
      setMessage('❌ Please enter a user email');
      return;
    }

    setLoading(true);
    setMessage('');

    try {
      console.log('🔐 Attempting to call updateUserCollectionHandles function...');
      console.log('Current admin user:', auth.currentUser?.email);

      // Guard against missing currentUser
      if (!auth.currentUser) {
        setMessage('❌ You must be logged in to perform this action.');
        setLoading(false);
        return;
      }
      
      // Parse comma-separated handles into array
      const handlesArray = collectionHandles
        .split(',')
        .map(h => h.trim())
        .filter(h => h.length > 0);
      
      console.log('Calling function with:', { 
        email: userEmail.trim(), 
        collectionHandles: handlesArray 
      });

      // Get the current user's ID token for authentication
      const idToken = await auth.currentUser.getIdToken();
      
      const response = await fetch(`${API_BASE_URL}/.netlify/functions/update-user-collection-handles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({
          email: userEmail.trim(),
          collectionHandles: handlesArray
        })
      });

      const result = await response.json();
      
      if (!response.ok) {
        console.error('Server response:', response.status, result);
        throw new Error(result.error || result.message || `Server error: ${response.status}`);
      }
      
      console.log('✅ Function result:', result);
      setMessage(`✅ ${result.message}`);
      
      // Clear form
      setUserEmail('');
      setCollectionHandles('');
      
    } catch (error) {
      console.error('❌ Full error object:', error);
      console.error('Error message:', error.message);
      setMessage(`❌ Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="collection-handles-manager">
      <h3>Update Collection Handle(s)</h3>
      <p>
        This will remove all and add only what you type below.
      </p>
      
      <div style={{ marginTop: '20px' }}>
        <label >
          User Email:
           </label>
          <input
            type="email"
            value={userEmail}
            onChange={(e) => setUserEmail(e.target.value)}
            placeholder="user@example.com"
            disabled={loading}
           
          />
       

        <label>
          Collection Handles<span className="sub">(comma-separated):</span>
          <textarea
            value={collectionHandles}
            onChange={(e) => setCollectionHandles(e.target.value)}
            placeholder="handle-1, handle-2, handle-3"
            disabled={loading}
            rows={1}
            
          />
        </label>

        <button
          onClick={handleUpdateCollectionHandles}
          disabled={loading}
          style={{
            marginTop: '16px',
            padding: '10px 20px',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: loading ? 'not-allowed' : 'pointer',
            opacity: loading ? 0.6 : 1
          }}
        >
          {loading ? 'Updating...' : 'Update Collection Handles'}
        </button>

        {message && (
          <div style={{
            marginTop: '16px',
            padding: '12px',
            borderRadius: '4px',
            backgroundColor: message.includes('✅') ? '#d4edda' : '#f8d7da',
            color: message.includes('✅') ? '#155724' : '#721c24',
            border: `1px solid ${message.includes('✅') ? '#c3e6cb' : '#f5c6cb'}`
          }}>
            {message}
          </div>
        )}
      </div>
    </div>
  );
};

export default CollectionHandlesManager;